import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import './ResetPassword.css';

interface ResetPasswordProps {
  onSuccess: () => void;
}

const ResetPassword: React.FC<ResetPasswordProps> = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState<{ type: 'success' | 'error'; message: string; title?: string }>({ type: 'success', message: '' });

  useEffect(() => {
    // Check if we have a valid token in sessionStorage (set by App.tsx)
    const token = sessionStorage.getItem('recovery_access_token');
    
    if (!token) {
      setIsTokenValid(false);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Get the recovery token
      const accessToken = sessionStorage.getItem('recovery_access_token');
      const refreshToken = sessionStorage.getItem('recovery_refresh_token');
      
      if (!accessToken) {
        setError('Invalid or expired reset link. Please request a new one.');
        return;
      }

      // First, set the session with the recovery token
      const { createClient } = await import('@supabase/supabase-js');
      const supabase = createClient(
        import.meta.env.VITE_SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_ANON_KEY
      );
      
      // Set the session using the recovery tokens
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken || accessToken
      });

      if (sessionError) {
        setError('Failed to verify reset link. Please request a new one.');
        return;
      }

      // Now update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        setError('Failed to reset password. Please try again.');
        return;
      }
      
      // Clear the recovery tokens
      sessionStorage.removeItem('recovery_access_token');
      sessionStorage.removeItem('recovery_refresh_token');
      
      // Show success message and redirect
      setModalConfig({ 
        type: 'success', 
        message: 'Password reset successful! Please log in with your new password.',
        title: 'Success!'
      });
      setShowModal(true);
    } catch (error) {
      setError('Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isTokenValid) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="reset-password-card">
            <div className="error-icon">❌</div>
            <h2>Invalid Reset Link</h2>
            <p className="error-message">
              This password reset link is invalid or has expired.
            </p>
            <button 
              onClick={onSuccess}
              className="back-to-login-button"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="reset-header">
            <div className="logo">🔐</div>
            <h1>Set New Password</h1>
            <p className="tagline">Your new password must be at least 6 characters</p>
          </div>

          <form onSubmit={handleSubmit} className="reset-form">
            {error && <div className="error-alert">{error}</div>}
            
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <button 
              type="submit" 
              disabled={isLoading} 
              className="submit-button"
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        </div>
      </div>
      
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          onSuccess();
        }}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
      />
    </div>
  );
};

export default ResetPassword;